/**
 * Matomo event tracking helpers. Avoids repeating the _paq boilerplate
 * at call sites and keeps the surface typed.
 *
 * Self-hosted Matomo at https://analytics.ideawarehouse.ca, site id 8.
 * The tracker script is loaded by the inline <Script> block in
 * src/app/layout.tsx; this file is the runtime API for emitting events
 * from client components.
 *
 * All helpers re-read window._paq on each call, so they're safe to
 * invoke before the tracker has finished loading.
 */

type Paq = (string | number | boolean | object | undefined)[][];

function paq(): Paq {
  if (typeof window === "undefined") return [];
  // Matomo seeds window._paq itself; we fall through to a fresh array
  // if a caller fires an event before the inline script has run.
  const w = window as unknown as { _paq?: Paq };
  return w._paq ?? [];
}

export function trackEvent(
  category: string,
  action: string,
  name?: string,
  value?: number,
): void {
  const args: (string | number)[] = ["trackEvent", category, action];
  if (name !== undefined) args.push(name);
  if (value !== undefined) args.push(value);
  paq().push(args);
}

/**
 * Matomo Content Tracking helpers.
 *
 * Content Tracking uses data attributes on DOM elements:
 *   data-track-content              — marks a content block
 *   data-content-name="feed.news"   — stable identifier (not a title)
 *   data-content-piece="slug"       — what specific content
 *   data-content-target="url"       — where the content links to
 *
 * Matomo scans for these on page load once trackVisibleContentImpressions
 * has been called. After dynamic appends, call rescanContent() to pick
 * up newly-rendered blocks.
 */

/** Rescan a container after dynamic content loads. Pass a node to scope
 *  the scan; passing undefined sweeps the whole viewport (heavier). */
export function rescanContent(node?: HTMLElement | null): void {
  if (node) {
    paq().push(["trackContentImpressionsWithinNode", node]);
  } else {
    paq().push(["trackVisibleContentImpressions", true, 750]);
  }
}

/** Manually track a content interaction (follow, compare, submit). */
export function trackContentInteraction(
  interaction: string,
  contentName: string,
  contentPiece?: string,
  contentTarget?: string,
): void {
  paq().push([
    "trackContentInteraction",
    interaction,
    contentName,
    contentPiece,
    contentTarget,
  ]);
}

/** Track a site search query. Pair with trackEvent for funnel reports. */
export function trackSiteSearch(
  keyword: string,
  category?: string,
  resultCount?: number,
): void {
  paq().push([
    "trackSiteSearch",
    keyword,
    category ?? false,
    resultCount ?? false,
  ]);
}

/**
 * Design-cohort tagging. Stamps the current visit with a versioned "design
 * version" so a Matomo Segment ("Design Version is parkpage-2026-07") can
 * isolate the cohort of visitors who experienced a given redesign — and fires
 * a countable event for at-a-glance volume.
 *
 * DESIGN_VERSION_DIMENSION is the index of the Visit-scoped "Design Version"
 * custom dimension configured in Matomo (site 8). Kept here alongside the other
 * hard-coded Matomo config (URL, site id in layout.tsx). The dimension is
 * Visit-scoped, so setting it after the initial trackPageView still tags the
 * whole visit via the follow-up event.
 */
const DESIGN_VERSION_DIMENSION = 1;

export function trackDesignCohort(version: string): void {
  paq().push(["setCustomDimension", DESIGN_VERSION_DIMENSION, version]);
  paq().push(["trackEvent", "Design", "Park page view", version]);
}
